import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import Loader from 'react-loader-spinner';
import DateRangePicker from 'rsuite/DateRangePicker';
import 'rsuite/dist/rsuite.min.css';
import SidebarComponent from '../SidebarComponent/SidebarComponent';
import ViewProfile from '../ViewProfile';
import HomeAudioRequestDrawer from './HomeAudioRequestDrawer';
import HomeAudioCsvImportModal from './HomeAudioCsvImportModal';
import ComplaintsComponent from '../ComplaintsComponent/ComplaintsComponent';
import { showComplaintDetailsAction } from '../../actions/complaintsDataAction';
import { exportInstallationRequests, fetchInstallationList, submitInstallationBulkAction } from '../../actions/installationAction';
import {
    BULK_ACTIONS,
    HA_PAGE_SIZE,
    HOME_AUDIO_BOARD,
    actionLabel,
    UNBUCKETED_VALUE,
    buildListBody,
    downloadCsv,
    formatAge,
    formatAmount,
    formatDate,
    listRowBadges,
    sourceLabel,
    ageStatusLabel,
} from './homeAudioUtils';
import './HomeAudioComponent.css';

const { allowedRange } = DateRangePicker;

// Same gate the sidebar uses for International. UX only — the export API returns 403 too.
const ADMIN_ROLE_IDS = ['1', '2'];

class HomeAudioComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            rows: [],
            total: 0,
            noPage: 0,
            page: 1,
            buckets: [],
            sources: [],
            bucket: '',
            source: '',
            startDate: null,
            endDate: null,
            searchInput: '',
            search: '',
            isLoading: false,
            error: null,
            message: null,
            isExporting: false,
            exportError: null,
            openRequestId: null,
            tab: 'requests',
            selectedIds: [],
            bulkAction: '',
            isBulkSubmitting: false,
            moveOnExport: false,
            csvOpen: false,
        };
    }

    // Selection is per page: loading a new page clears it, so a CS agent can never act on
    // rows they are no longer looking at.
    toggleRow = (id) => {
        this.setState((prev) => ({
            selectedIds: prev.selectedIds.indexOf(id) > -1 ? prev.selectedIds.filter((x) => x !== id) : [...prev.selectedIds, id],
        }));
    };

    toggleAllRows = () => {
        this.setState((prev) => ({ selectedIds: prev.selectedIds.length === prev.rows.length ? [] : prev.rows.map((row) => row.request_id) }));
    };

    handleBulkAction = async () => {
        const { selectedIds, bulkAction } = this.state;
        if (!bulkAction || selectedIds.length === 0) {
            return;
        }
        this.setState({ isBulkSubmitting: true, error: null });
        const result = await submitInstallationBulkAction({ request_ids: selectedIds, action: bulkAction });
        if (!result.success) {
            this.setState({ isBulkSubmitting: false, exportError: result.error });
            return;
        }
        this.setState({ isBulkSubmitting: false, selectedIds: [], bulkAction: '', message: result.data.message }, () =>
            this.loadList(this.state.page)
        );
    };

    renderBulkBar = () => {
        const { selectedIds, bulkAction, isBulkSubmitting } = this.state;
        if (selectedIds.length === 0) {
            return null;
        }
        return (
            <div className="ha-bulkbar">
                <span className="ha-bulkbar-count">{selectedIds.length} selected</span>
                <select className="ha-select" value={bulkAction} onChange={(e) => this.setState({ bulkAction: e.target.value })}>
                    <option value="">Move selected to…</option>
                    {BULK_ACTIONS.map((action) => (
                        <option key={action} value={action}>
                            {actionLabel(action)}
                        </option>
                    ))}
                </select>
                <button className="ha-btn ha-btn-primary" disabled={!bulkAction || isBulkSubmitting} onClick={this.handleBulkAction}>
                    {isBulkSubmitting ? 'Applying…' : 'Apply'}
                </button>
                <button className="ha-btn ha-btn-ghost" disabled={isBulkSubmitting} onClick={() => this.setState({ selectedIds: [], bulkAction: '' })}>
                    Clear
                </button>
            </div>
        );
    };

    // Leaving the Complaints tab must not leave a complaint detail open in redux for the
    // next visit, so the shared flag is reset on every switch.
    selectTab = (tab) => {
        if (tab === this.state.tab) return;
        this.props.showComplaintDetailsAction(false);
        this.setState({ tab });
    };

    renderTabs = () => {
        const { tab, total } = this.state;
        return (
            <div className="ha-tabs" role="tablist">
                <button role="tab" aria-selected={tab === 'requests'} className={`ha-tab ${tab === 'requests' ? 'ha-tab--on' : ''}`} onClick={() => this.selectTab('requests')}>
                    Requests <span className="ha-tab-count">{total}</span>
                </button>
                <button role="tab" aria-selected={tab === 'complaints'} className={`ha-tab ${tab === 'complaints' ? 'ha-tab--on' : ''}`} onClick={() => this.selectTab('complaints')}>
                    Complaints
                </button>
            </div>
        );
    };

    componentDidMount() {
        this.loadList(1);
    }

    currentFilters = (page) => ({
        page: page || this.state.page,
        limit: HA_PAGE_SIZE,
        bucket: this.state.bucket,
        source: this.state.source,
        startDate: this.state.startDate ? moment(this.state.startDate).format('YYYY-MM-DD') : null,
        endDate: this.state.endDate ? moment(this.state.endDate).format('YYYY-MM-DD') : null,
        search: this.state.search,
    });

    loadList = async (page = 1) => {
        this.setState({ isLoading: true, error: null, exportError: null });
        const result = await fetchInstallationList(buildListBody(this.currentFilters(page)));
        if (!result.success) {
            this.setState({ error: result.error, isLoading: false });
            return;
        }
        const data = result.data || {};
        const filters = data.filters || {};
        this.setState({
            rows: data.installation_data || [],
            total: data.total || 0,
            noPage: data.no_page || 0,
            page: data.page_no || page,
            buckets: filters.buckets || [],
            sources: filters.sources || [],
            // The API answers an empty list with guidance when a search term is unusable.
            message: (data.installation_data || []).length === 0 ? result.message : null,
            // Cleared on every load: a selection must never outlive the rows it was made on.
            selectedIds: [],
            isLoading: false,
        });
    };

    handleFilterChange = (field, value) => {
        this.setState({ [field]: value }, () => this.loadList(1));
    };

    handleDateSelect = (value) => {
        if (!value || value.length !== 2) {
            this.setState({ startDate: null, endDate: null }, () => this.loadList(1));
            return;
        }
        this.setState({ startDate: value[0], endDate: value[1] }, () => this.loadList(1));
    };

    handleSearchSubmit = () => {
        this.setState({ search: this.state.searchInput.trim() }, () => this.loadList(1));
    };

    handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            this.handleSearchSubmit();
        }
    };

    handleClearSearch = () => {
        this.setState({ searchInput: '', search: '' }, () => this.loadList(1));
    };

    handlePageChange = (page) => {
        if (page < 1 || page > this.state.noPage) return;
        this.loadList(page);
    };

    // Exports exactly the filter state on screen; the CSV comes back inside the JSON body.
    handleExport = async () => {
        this.setState({ isExporting: true, exportError: null });
        const result = await exportInstallationRequests({
            ...buildListBody(this.currentFilters(1)),
            move_to_assigned: this.state.moveOnExport,
        });
        if (!result.success) {
            this.setState({ isExporting: false, exportError: result.error });
            return;
        }
        downloadCsv(result.data.csv, result.data.file_name);
        const moved = result.data.moved;
        this.setState({
            isExporting: false,
            message: moved === null || moved === undefined ? null : `${moved} request(s) moved to Installation Assigned to Service Partner.`,
        });
        if (moved !== null && moved !== undefined) {
            this.loadList(this.state.page);
        }
    };

    renderToolbar = (isAdmin) => {
        const { bucket, source, searchInput, search, startDate, endDate, buckets, sources, isExporting } = this.state;
        return (
            <div className="ha-toolbar">
                <div className="ha-search">
                    <input
                        className="ha-input ha-search-input"
                        type="text"
                        autoComplete="off"
                        placeholder="Search request ID, phone or customer name…"
                        value={searchInput}
                        onChange={(e) => this.setState({ searchInput: e.target.value })}
                        onKeyDown={this.handleSearchKeyDown}
                    />
                    <button className="ha-btn" onClick={this.handleSearchSubmit}>
                        Search
                    </button>
                    {search && (
                        <button className="ha-btn ha-btn-ghost" onClick={this.handleClearSearch}>
                            Clear
                        </button>
                    )}
                </div>

                <select className="ha-select" value={bucket} onChange={(e) => this.handleFilterChange('bucket', e.target.value)}>
                    <option value="">All buckets</option>
                    {buckets.map((item) => (
                        <option key={item.id} value={item.id}>
                            {item.name}
                        </option>
                    ))}
                    <option value={UNBUCKETED_VALUE}>Unpaid — no bucket</option>
                </select>

                <select className="ha-select" value={source} onChange={(e) => this.handleFilterChange('source', e.target.value)}>
                    <option value="">All sources</option>
                    {sources.map((item) => (
                        <option key={item} value={item}>
                            {sourceLabel(item)}
                        </option>
                    ))}
                </select>

                <div className="ha-date">
                    <DateRangePicker
                        showOneCalendar
                        format="dd/MM/yyyy"
                        placeholder="Created date range"
                        placement="bottomStart"
                        menuClassName="ha-date-popup"
                        shouldDisableDate={allowedRange(
                            moment(new Date(new Date().setDate(new Date().getDate() - 365))).format('YYYY-MM-DD'),
                            moment(new Date()).format('YYYY-MM-DD')
                        )}
                        value={startDate && endDate ? [new Date(startDate), new Date(endDate)] : null}
                        onOk={this.handleDateSelect}
                        onClean={this.handleDateSelect}
                    />
                </div>

                <div className="ha-toolbar-right">
                    <button
                        className="ha-btn"
                        disabled={!isAdmin || isExporting}
                        title={isAdmin ? 'Export the current filter state in Jeeves format' : 'Admin access required'}
                        onClick={this.handleExport}
                    >
                        {!isAdmin && <i className="fas fa-lock" />} {isExporting ? 'Exporting…' : 'Export to Jeeves Format'}
                    </button>
                    {isAdmin && (
                        <label className="ha-export-move" title="The partner has the job the moment the file is sent">
                            <input
                                type="checkbox"
                                checked={this.state.moveOnExport}
                                onChange={(e) => this.setState({ moveOnExport: e.target.checked })}
                            />
                            Also assign to service partner
                        </label>
                    )}
                    <button className="ha-btn" onClick={() => this.setState({ csvOpen: true })}>
                        + Create Manual Request
                    </button>
                </div>
            </div>
        );
    };

    renderRow = (row) => {
        const badges = listRowBadges(row);
        return (
            <tr key={row.request_id} onClick={() => this.setState({ openRequestId: row.request_id })}>
                <td className="ha-check-cell" onClick={(e) => e.stopPropagation()}>
                    <input
                        type="checkbox"
                        aria-label={`Select ${row.request_no}`}
                        checked={this.state.selectedIds.indexOf(row.request_id) > -1}
                        onChange={() => this.toggleRow(row.request_id)}
                    />
                </td>
                <td>
                    <div className="ha-strong">{row.request_no}</div>
                    <div className="ha-sub">{formatDate(row.created_at)}</div>
                    {badges.length > 0 && (
                        <div className="ha-badge-row">
                            {badges.map((badge) => (
                                <span key={badge.key} className={`ha-chip ha-chip-${badge.tone}`}>
                                    {badge.label}
                                </span>
                            ))}
                        </div>
                    )}
                </td>
                <td>
                    <div>{row.customer_name || '—'}</div>
                    <div className="ha-sub ha-mono">{row.mobile || '—'}</div>
                </td>
                <td>
                    <div>{row.product_model || '—'}</div>
                    <div className="ha-sub">{row.sku || ''}</div>
                </td>
                <td>
                    <div>{[row.city_name, row.state_name].filter(Boolean).join(', ') || '—'}</div>
                    <div className="ha-sub">{row.pincode || ''}</div>
                </td>
                <td>{row.source_label || sourceLabel(row.source)}</td>
                <td>
                    <span className="ha-badge ha-badge-bucket">{row.bucket_name || 'Unpaid'}</span>
                </td>
                <td>
                    {row.is_manual ? (
                        <>
                            <span className="ha-badge ha-badge-manual">manual</span>
                            <div className="ha-sub">prepaid on channel</div>
                        </>
                    ) : (
                        <>
                            <span className={`ha-badge ha-pay-${row.payment_status}`}>{row.payment_status}</span>
                            <div className="ha-sub">{formatAmount(row.amount)}</div>
                        </>
                    )}
                </td>
                <td>{row.scheduled_date ? formatDate(row.scheduled_date) : '—'}</td>
                <td>
                    <span className={`ha-age ha-age-${(row.ageing && row.ageing.status) || 'none'}`} title={ageStatusLabel(row.ageing && row.ageing.status)}>
                        {formatAge(row.ageing)}
                    </span>
                </td>
            </tr>
        );
    };

    renderPagination = () => {
        const { page, noPage, total } = this.state;
        const from = total === 0 ? 0 : (page - 1) * HA_PAGE_SIZE + 1;
        const to = Math.min(total, page * HA_PAGE_SIZE);
        return (
            <div className="ha-pagination">
                <span className="ha-sub">{total > 0 ? `Showing ${from}–${to} of ${total}` : 'Showing 0 of 0'}</span>
                <div className="ha-pagination-controls">
                    <button className="ha-btn ha-btn-ghost" disabled={page <= 1} onClick={() => this.handlePageChange(page - 1)}>
                        Previous
                    </button>
                    <span className="ha-sub">Page {page} of {Math.max(1, noPage)}</span>
                    <button className="ha-btn ha-btn-ghost" disabled={page >= noPage} onClick={() => this.handlePageChange(page + 1)}>
                        Next
                    </button>
                </div>
            </div>
        );
    };

    renderRequests = (isAdmin) => {
        const { rows, isLoading, error, message, exportError, selectedIds } = this.state;
        return (
            <>
                {this.renderToolbar(isAdmin)}
                {this.renderBulkBar()}

                <div className="ha-pii-note">
                    <i className="fas fa-shield-alt" />{' '}
                    {isAdmin
                        ? 'Admin view — customer numbers are shown in full and the Jeeves export is unmasked.'
                        : 'Customer phone numbers are masked in this view. Full details go out only in the admin Jeeves export.'}
                </div>

                {exportError && <div className="ha-error">{exportError}</div>}

                {isLoading ? (
                    <div className="ha-loader">
                        <Loader type="Oval" color="Grey" height={120} width={120} />
                    </div>
                ) : error ? (
                    <div className="ha-error">{error}</div>
                ) : (
                    <div className="ha-card">
                        <div className="ha-table-wrap">
                            <table className="ha-table">
                                <thead>
                                    <tr>
                                        <th className="ha-check-cell">
                                            <input
                                                type="checkbox"
                                                aria-label="Select every request on this page"
                                                checked={rows.length > 0 && selectedIds.length === rows.length}
                                                onChange={this.toggleAllRows}
                                            />
                                        </th>
                                        <th>Request</th>
                                        <th>Customer</th>
                                        <th>Product</th>
                                        <th>Location</th>
                                        <th>Source</th>
                                        <th>Bucket</th>
                                        <th>Payment</th>
                                        <th>Scheduled</th>
                                        <th>Age</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr className="ha-row-empty">
                                            <td colSpan="10">{message || 'No installation requests match these filters.'}</td>
                                        </tr>
                                    ) : (
                                        rows.map(this.renderRow)
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {this.renderPagination()}
                    </div>
                )}
            </>
        );
    };

    render() {
        const isAdmin = ADMIN_ROLE_IDS.indexOf(localStorage.getItem('userRoleId')) > -1;
        const { openRequestId, total, tab } = this.state;

        return (
            <div>
                {window.innerWidth > 600 && <SidebarComponent sideProp={'HA'} />}
                <ViewProfile />

                <div className="content-dashboard ha-page">
                    <div className="content-title">
                        <h3>Home Audio</h3>
                        {tab === 'requests' && <span className="ha-count">{total} request{total === 1 ? '' : 's'}</span>}
                    </div>
                    {this.renderTabs()}

                    {tab === 'requests' ? this.renderRequests(isAdmin) : <ComplaintsComponent embedded board={HOME_AUDIO_BOARD} />}
                </div>

                {this.state.csvOpen && (
                    <HomeAudioCsvImportModal
                        onClose={() => this.setState({ csvOpen: false })}
                        onImported={() => this.loadList(1)}
                    />
                )}

                {openRequestId && (
                    <HomeAudioRequestDrawer
                        requestId={openRequestId}
                        onClose={() => this.setState({ openRequestId: null })}
                        onActionDone={() => this.loadList(this.state.page)}
                    />
                )}
            </div>
        );
    }
}

export default connect(null, { showComplaintDetailsAction })(HomeAudioComponent);
